import { type Ref, ref } from 'vue';

export type SwipeDirection = 'prev' | 'next';

export interface HorizontalSwipeOptions {
  edgeTapZone?: number;
  maxTapDistance?: number;
  maxTapDuration?: number;
  onEdgeTap?: (direction: SwipeDirection) => void;
}

interface PointerAnchor {
  pointerId: number;
  x: number;
  y: number;
  time: number;
}

export function useHorizontalSwipe(options: HorizontalSwipeOptions = {}): {
  swipeAnchor: Ref<PointerAnchor | null>;
  onPointerDown: (event: PointerEvent) => void;
  onPointerUp: (event: PointerEvent) => void;
} {
  const swipeAnchor = ref<PointerAnchor | null>(null);
  const edgeTapZone = options.edgeTapZone ?? 96;
  const maxTapDistance = options.maxTapDistance ?? 12;
  const maxTapDuration = options.maxTapDuration ?? 400;

  function onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    swipeAnchor.value = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      time: performance.now(),
    };
  }

  function onPointerUp(event: PointerEvent): void {
    const anchor = swipeAnchor.value;
    swipeAnchor.value = null;
    if (!anchor || anchor.pointerId !== event.pointerId) {
      return;
    }

    const dx = Math.abs(event.clientX - anchor.x);
    const dy = Math.abs(event.clientY - anchor.y);
    if (
      dx > maxTapDistance ||
      dy > maxTapDistance ||
      performance.now() - anchor.time > maxTapDuration
    ) {
      return;
    }

    const element = event.currentTarget;
    if (!(element instanceof HTMLElement)) {
      return;
    }

    const rect = element.getBoundingClientRect();
    if (event.clientX - rect.left <= edgeTapZone) {
      options.onEdgeTap?.('prev');
    } else if (rect.right - event.clientX <= edgeTapZone) {
      options.onEdgeTap?.('next');
    }
  }

  return { swipeAnchor, onPointerDown, onPointerUp };
}
